import { DefaultButton, Dialog, DialogType, getTheme, PrimaryButton, Stack, Text } from '@fluentui/react';
import React, { useEffect, useState } from 'react';
import IvicosStrings from 'kits/language/stringKit';
import { AWAY_COUNTDOWN_SECONDS } from 'shared-state/identity/constants/activityTimerConstants';

export interface IAwayExtensionModalProps {
    isOpen: boolean;
    onNo: () => void;
    onYes: () => void;
    onDismiss: () => void;
    onTimeout: () => void;
}

function formatCountdown(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Modal shown after 20 min on away status (when isUserSelectedAwayManually).
 * Asks "Would you like to change your status?", 60-min countdown, No/Yes.
 * Yes → available + office (back to At Work). No/X → stay away, reset 20-min timer. Timeout → OOO Today, lobby redirect.
 */
const AwayExtensionModal: React.FC<IAwayExtensionModalProps> = ({ isOpen, onNo, onYes, onDismiss, onTimeout }) => {
    const theme = getTheme();
    const [remainingSeconds, setRemainingSeconds] = useState(AWAY_COUNTDOWN_SECONDS);

    useEffect(() => {
        if (!isOpen) return;
        setRemainingSeconds(AWAY_COUNTDOWN_SECONDS);
        const id = setInterval(() => {
            setRemainingSeconds((prev) => {
                const next = prev - 1;
                if (next <= 0) {
                    clearInterval(id);
                    onTimeout();
                    return 0;
                }
                return next;
            });
        }, 1000);
        return () => clearInterval(id);
    }, [isOpen, onTimeout]);

    const handleNo = () => {
        onNo();
    };

    const handleYes = () => {
        onYes();
    };

    const handleDismiss = () => {
        onNo();
        onDismiss();
    };

    return (
        <Dialog
            hidden={!isOpen}
            onDismiss={handleDismiss}
            maxWidth="450px"
            minWidth="450px"
            dialogContentProps={{
                type: DialogType.close,
                title: IvicosStrings.awayExtensionModalTitle,
                closeButtonAriaLabel: IvicosStrings.awayExtensionModalCloseAriaLabel
            }}
            modalProps={{ isBlocking: true }}
            styles={{ main: { borderRadius: 10, boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)' } }}
        >
            <Stack tokens={{ childrenGap: 40 }}>
                <Text variant="medium" style={{ color: theme.palette.black }}>
                    {IvicosStrings.awayExtensionModalBody}
                </Text>
                <Stack tokens={{ childrenGap: 4 }}>
                    <Text variant="medium" style={{ color: theme.palette.black }}>
                        {IvicosStrings.awayExtensionModalLogoutLabel}
                    </Text>
                    <Text variant="large" style={{ fontWeight: 600 }}>
                        {formatCountdown(remainingSeconds)}
                    </Text>
                </Stack>
                <Stack horizontal tokens={{ childrenGap: 8 }} horizontalAlign="end" style={{ marginTop: 18 }}>
                    <DefaultButton text={IvicosStrings.awayExtensionModalNo} onClick={handleNo} />
                    <PrimaryButton text={IvicosStrings.awayExtensionModalYes} onClick={handleYes} />
                </Stack>
            </Stack>
        </Dialog>
    );
};

export default AwayExtensionModal;
